<script lang="ts">
// 2026-09-17：订单的分组/展开表沿用原交互，仅统一紧凑列宽、名称及订单复制显示。
import { Comment, Fragment, Text, defineComponent, h, VNode, provide, computed, resolveComponent, ref } from 'vue'
import { Table } from 'ant-design-vue'
import { useOrderCellTitles } from '@/utils/useOrderCellTitles'
import { orderFitColumns } from '@/utils/orderTableFit'
import ListText from './ListText.vue'
import { resolveGroupedColumns, readableTableColumns } from '@/utils/responsiveTable'
import { useTableContainer } from '@/utils/useTableContainer'
import { useActionColumns, fitActionColumns } from '@/utils/useActionColumns'
// 2026-10-02：订单主清单启用顶部右侧分页，展开明细继续沿用原完整展示。
import { useListPagination } from '@/utils/useListPagination'

function hasContent(nodes: VNode[] | undefined): boolean {
  return !!nodes?.some(node => node.type !== Comment && (node.type === Fragment
    ? hasContent(node.children as VNode[])
    : node.type !== Text || String(node.children || '').trim().length > 0))
}
export default defineComponent({
  name: 'CompactOrderTable', inheritAttrs: false,
  props: { paginate:{type:Boolean,default:false}, dataSource:{type:Array,default:()=>[]}, columns: { type: Array, required: true },responsive:{type:Boolean,default:false},fitColumns:{type:Boolean,default:false},orderFit:{type:Boolean,default:false} },
  setup(props, { attrs, slots }) {
    // 2026-09-17：外容器可缩小，Ant列、分组表头和scroll.x使用同一容器分配结果。
    // 2026-09-17：默认原样透传正式Ant布局，只有显式清单启用自动列宽/高度；复制和原插槽保留。
    const {container,width,availableHeight}=useTableContainer(()=>props.responsive)
    const keyWidths=useOrderCellTitles(container,()=>props.orderFit)
    const {widths:actionWidths,isActionColumn,actionColumnKey}=useActionColumns(container)
    const actionColumns=computed(()=>fitActionColumns(props.orderFit?orderFitColumns(props.columns as any[],props.dataSource,keyWidths.value):props.columns as any[],actionWidths.value))
    provide('landiyun-responsive-table',computed(()=>props.responsive))
    provide('landiyun-order-fit',computed(()=>props.orderFit))
    const paging=useListPagination(()=>props.dataSource)
    const activeFilters=ref<Record<string,unknown[]|null>>({})
    const visibleTotal=computed(()=>{
      const columns=(props.columns as any[]).filter(column=>typeof column.onFilter==='function' && activeFilters.value[String(column.key ?? column.dataIndex)]?.length)
      return columns.length?props.dataSource.filter(row=>columns.every(column=>activeFilters.value[String(column.key ?? column.dataIndex)]!.some(value=>column.onFilter(value,row)))).length:paging.total.value
    })
    const tableChanged=(pagination:any,filters:any,sorter:any,extra:any)=>{
      if(extra?.action==='filter'){
        activeFilters.value=filters || {}
        paging.current.value=1
      }else if(extra?.action==='sort')paging.current.value=1
      if(typeof attrs.onChange==='function')attrs.onChange(pagination,filters,sorter,extra)
    }
    // 2026-10-06：分页入口使用全局统一组件，Table保留完整数据的排序/过滤及受控分页，仅隐藏内建页码。
    const withPagination=(table:VNode)=>props.paginate?h(Fragment,null,[
      h('div',{class:'admin-list-pagination'},[h(resolveComponent('APagination'),{
        current:paging.current.value,pageSize:paging.pageSize.value,total:visibleTotal.value,
        showSizeChanger:true,showQuickJumper:true,showLessItems:true,onChange:paging.change
      })]),table
    ]):table
    return () => {
      const tableAttrs={...attrs,onChange:tableChanged,dataSource:props.dataSource,...(props.paginate?{
        class:['admin-paged-order-table',attrs.class],
        pagination:{current:paging.current.value,pageSize:paging.pageSize.value,size:'default',position:['none'],
          showSizeChanger:true,showQuickJumper:true,showLessItems:true,onChange:paging.change}
      }:{})}
      const tableSlots={...slots,bodyCell:(scope:any)=>{
        const output=slots.bodyCell?.(scope)
        if(isActionColumn(scope.column))return h('div',{class:'admin-action-content','data-action-key':actionColumnKey(scope.column)},hasContent(output)?output:scope.text)
        if(hasContent(output))return output
        const field=scope.column.dataIndex
        if(['productName','title','orderNumber','orderId'].includes(field))
          return h(ListText,{value:scope.record[field],kind:['orderNumber','orderId'].includes(field)?'order':'product'})
        return undefined
      }}
      if(!props.responsive)return withPagination(h('div',{ref:container,'data-action-scope':'',style:{width:'100%',minWidth:0}},[h(Table as any,{...tableAttrs,columns:actionColumns.value,scroll:{...(attrs.scroll as any || {}),x:(attrs.scroll as any)?.x || 'max-content'}},tableSlots)]))
      // 2026-09-17：透传属性可来自模板短横线或render驼峰写法，两种写法都为选择/展开列预留宽度。
      const selection=(attrs.rowSelection ?? attrs['row-selection']) as any
      const selectionWidth=selection?Number(selection.columnWidth)||32:0
      const expandable=Boolean(slots.expandedRowRender || attrs.expandedRowRender || attrs['expanded-row-render']) && (attrs.showExpandColumn ?? attrs['show-expand-column'])!==false
      const expansionWidth=expandable?Number(attrs.expandColumnWidth ?? attrs['expand-column-width'])||32:0
      // Ant正文的竖向滚动条也占宽，按正文可用宽度分配，避免末列出现17px横滚。
      const body=container.value?.querySelector<HTMLElement>('.ant-table-body')
      const scrollbar=props.orderFit ? body?body.offsetWidth-body.clientWidth:17 : 0
      const layout=resolveGroupedColumns(props.fitColumns?readableTableColumns(actionColumns.value):actionColumns.value,Math.max(0,width.value-scrollbar),true,selectionWidth+expansionWidth)
      const configuredScroll=attrs.scroll as any || {}
      const maxBodyHeight=availableHeight.value===null?undefined:Math.max(80,availableHeight.value-54)
      const scrollY=maxBodyHeight===undefined?configuredScroll.y:typeof configuredScroll.y==='number'?Math.min(configuredScroll.y,maxBodyHeight):maxBodyHeight
      return withPagination(h('div',{ref:container,'data-action-scope':'',class:['responsive-order-container',{'order-fit-table':props.orderFit}],style:{width:'100%',maxWidth:'100%',minWidth:0}},[h(Table as any, { ...tableAttrs, columns:layout.columns, size: 'small', tableLayout: 'fixed',
        ...(selection?{rowSelection:{...selection,columnWidth:selectionWidth}}:{}),
        ...(expandable?{expandColumnWidth:expansionWidth}:{}),
        class: ['compact-order-table', tableAttrs.class], style: [attrs.style,{width:'100%',maxWidth:'100%',minWidth:0}],
        scroll: { ...configuredScroll, x:layout.width, ...(scrollY!==undefined?{y:scrollY}:{}) }
      }, tableSlots)]))
    }
  }
})
</script>
<style>
.compact-order-table .ant-table-thead > tr > th,
.compact-order-table .ant-table-tbody > tr > td { padding:6px; }
</style>
